/** Alignment of the profile stat cards, the same idea as the canvas smart guides. */

export interface StatRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** `axis: 'x'` is a vertical line at `at`; `axis: 'y'` is a horizontal line. */
export interface StatGuide {
  axis: 'x' | 'y';
  at: number;
  from: number;
  to: number;
}

const TOL = 6;

const xs = (r: StatRect) => [r.x, r.x + r.w / 2, r.x + r.w];
const ys = (r: StatRect) => [r.y, r.y + r.h / 2, r.y + r.h];

interface Hit {
  d: number;
  at: number;
  t: StatRect | null;
}

function closer(best: Hit | null, d: number, at: number, t: StatRect | null): Hit | null {
  if (Math.abs(d) > TOL) return best;
  if (!best || Math.abs(d) < Math.abs(best.d)) return { d, at, t };
  return best;
}

function stillOn(box: StatRect, at: number, axis: 'x' | 'y'): boolean {
  const vs = axis === 'x' ? xs(box) : ys(box);
  return vs.some((v) => Math.abs(v - at) < 0.5);
}

/**
 * Stick a card to edges and centers of the others (and to the tray's left, right, top and bottom).
 * While resizing, only the right and bottom edges move. `boardH` is the tray's inner height cap;
 * omit it and the card may still travel downward.
 */
export function snapStatBox(box: StatRect, others: StatRect[], boardW: number, mode: 'move' | 'resize', minW = 120, minH = 84, boardH = Number.POSITIVE_INFINITY): { box: StatRect; guides: StatGuide[] } {
  let { x, y, w, h } = box;
  const guides: StatGuide[] = [];
  let bestX: Hit | null = null;
  let bestY: Hit | null = null;
  const boardX = [0, boardW];
  const boardY = Number.isFinite(boardH) ? [0, boardH] : [0];

  if (mode === 'move') {
    for (const m of xs(box)) {
      for (const t of others) for (const v of xs(t)) bestX = closer(bestX, v - m, v, t);
      for (const v of boardX) bestX = closer(bestX, v - m, v, null);
    }
    for (const m of ys(box)) {
      for (const t of others) for (const v of ys(t)) bestY = closer(bestY, v - m, v, t);
      for (const v of boardY) bestY = closer(bestY, v - m, v, null);
    }
    if (bestX) x += bestX.d;
    if (bestY) y += bestY.d;
    x = Math.min(Math.max(0, x), Math.max(0, boardW - w));
    y = Math.max(0, y);
    if (Number.isFinite(boardH)) {
      h = Math.min(h, boardH);
      y = Math.min(y, Math.max(0, boardH - h));
    }
  } else {
    const right = x + w;
    const bottom = y + h;
    for (const t of others) for (const v of xs(t)) bestX = closer(bestX, v - right, v, t);
    for (const v of boardX) bestX = closer(bestX, v - right, v, null);
    for (const t of others) for (const v of ys(t)) bestY = closer(bestY, v - bottom, v, t);
    for (const v of boardY) bestY = closer(bestY, v - bottom, v, null);
    if (bestX) w += bestX.d;
    if (bestY) h += bestY.d;
    w = Math.min(Math.max(minW, w), Math.max(minW, boardW - x));
    h = Math.max(minH, h);
    if (Number.isFinite(boardH)) h = Math.min(h, Math.max(0, boardH - y));
  }

  const nb = { x, y, w, h };
  if (bestX && stillOn(nb, bestX.at, 'x')) {
    const t = bestX.t;
    guides.push({ axis: 'x', at: bestX.at, from: t ? Math.min(nb.y, t.y) : nb.y, to: t ? Math.max(nb.y + nb.h, t.y + t.h) : nb.y + nb.h });
  }
  if (bestY && stillOn(nb, bestY.at, 'y')) {
    const t = bestY.t;
    guides.push({ axis: 'y', at: bestY.at, from: t ? Math.min(nb.x, t.x) : nb.x, to: t ? Math.max(nb.x + nb.w, t.x + t.w) : nb.x + nb.w });
  }
  return { box: nb, guides };
}
